import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LayoutPanelLeft, Plus, FileStack, Palette, Image as ImageIcon, CheckCircle2, XCircle, AlertTriangle, Send, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardHeader, EmptyState } from "@/components/common";

const STAGES = ["concept", "documentation", "selections", "rendering", "qa", "ready", "delivered"];

export default function DesignPackets() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: async () => (await api.get("/companies")).data });
  const { data: packets = [], isLoading } = useQuery({ queryKey: ["design-packets"], queryFn: async () => (await api.get("/design-packets")).data });

  const refresh = () => qc.invalidateQueries({ queryKey: ["design-packets"] });

  return (
    <div data-testid="design-packets-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.12em] uppercase text-ink-muted"><Palette className="w-3.5 h-3.5 text-brand" /> Interior &amp; Architecture</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink mt-1">Design Packets</h1>
          <p className="text-sm text-ink-muted mt-1">Drawing register, selections schedule, renders — assembled and gated by owner approval.</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="new-packet-btn" className="flex items-center gap-2 bg-brand text-white px-4 py-2 text-sm font-semibold hover:bg-brand-dark transition-colors"><Plus className="w-4 h-4" /> New Packet</button>
      </div>

      {showNew && <NewPacketForm companies={companies} onClose={() => setShowNew(false)} onCreated={(p) => { refresh(); setSelected(p); setShowNew(false); }} />}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="space-y-2" data-testid="packet-list">
          {isLoading && <div className="text-sm text-ink-muted">Loading…</div>}
          {!isLoading && packets.length === 0 && <EmptyState icon={FileStack} title="No design packets yet" hint="Create one to start the drawing register." />}
          {packets.map((p) => (
            <button key={p.id} onClick={() => setSelected(p)} data-testid={`packet-${p.id}`}
              className={`w-full text-left border p-3 transition-colors ${selected?.id === p.id ? "border-brand bg-brand/5" : "border-border hover:border-brand/50"}`}>
              <div className="flex items-center justify-between"><span className="font-medium text-ink text-sm truncate">{p.name}</span><ChevronRight className="w-4 h-4 text-ink-muted shrink-0" /></div>
              <div className="text-xs text-ink-muted mt-0.5">{p.client_name}</div>
              <div className="flex items-center gap-2 mt-2">
                <StageBadge stage={p.stage} />
                {p.summary_counts?.selections_at_risk > 0 && <span className="text-[10px] text-st-warning flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {p.summary_counts.selections_at_risk} at risk</span>}
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2">
          {selected ? <PacketDetail key={selected.id} pid={selected.id} onChange={refresh} /> : <EmptyState icon={LayoutPanelLeft} title="Select a packet" hint="Pick a design packet to manage its drawings, selections and renders." />}
        </div>
      </div>
    </div>
  );
}

function StageBadge({ stage }) {
  const idx = STAGES.indexOf(stage);
  return <span className="text-[10px] font-semibold uppercase tracking-wider border border-brand text-brand px-1.5 py-0.5">{stage} · {idx + 1}/{STAGES.length}</span>;
}

function NewPacketForm({ companies, onClose, onCreated }) {
  const [form, setForm] = useState({ company_id: companies[0]?.id || "", name: "", client_name: "", project_ref: "" });
  const mut = useMutation({
    mutationFn: async () => (await api.post("/design-packets", form)).data,
    onSuccess: (p) => { toast.success("Design packet created"); onCreated(p); },
    onError: () => toast.error("Failed to create packet"),
  });
  return (
    <Card testId="new-packet-form" className="mb-4">
      <CardHeader title="New Design Packet" icon={Plus} />
      <div className="grid sm:grid-cols-2 gap-2">
        <select value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} data-testid="np-company" className="border border-border px-2 py-2 text-sm bg-white focus:border-brand focus:outline-none">
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Client name" data-testid="np-client" className="border border-border px-2 py-2 text-sm focus:border-brand focus:outline-none" />
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Packet name" data-testid="np-name" className="border border-border px-2 py-2 text-sm focus:border-brand focus:outline-none" />
        <input value={form.project_ref} onChange={(e) => setForm({ ...form, project_ref: e.target.value })} placeholder="project_ref (optional)" data-testid="np-ref" className="border border-border px-2 py-2 text-sm focus:border-brand focus:outline-none" />
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => mut.mutate()} disabled={!form.name || !form.company_id || mut.isPending} data-testid="np-create" className="bg-brand text-white px-4 py-2 text-sm font-semibold disabled:opacity-60">{mut.isPending ? "Creating…" : "Create"}</button>
        <button onClick={onClose} className="border border-border px-4 py-2 text-sm">Cancel</button>
      </div>
    </Card>
  );
}

function PacketDetail({ pid, onChange }) {
  const qc = useQueryClient();
  const { data: p, isLoading } = useQuery({ queryKey: ["packet", pid], queryFn: async () => (await api.get(`/design-packets/${pid}`)).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["packet", pid] }); onChange?.(); };

  const post = useMutation({ mutationFn: ({ path, body }) => api.post(`/design-packets/${pid}${path}`, body), onSuccess: refresh });
  const submit = useMutation({
    mutationFn: () => api.post(`/design-packets/${pid}/submit`),
    onSuccess: () => { toast.success("Submitted for owner approval"); refresh(); },
    onError: () => toast.error("Submit failed"),
  });

  if (isLoading || !p) return <div className="text-sm text-ink-muted">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="packet-detail">
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div><div className="font-heading text-xl font-bold text-ink">{p.name}</div><div className="text-sm text-ink-muted">{p.client_name} {p.project_ref && `· ${p.project_ref}`}</div></div>
          <StageBadge stage={p.stage} />
        </div>
        <div className="grid grid-cols-4 gap-2 mt-4 text-center">
          {[["Drawings", p.summary_counts.drawings], ["Selections", p.summary_counts.selections], ["At risk", p.summary_counts.selections_at_risk], ["Renders", `${p.summary_counts.renders_complete}/${p.summary_counts.renders}`]].map(([l, v]) => (
            <div key={l} className="border border-border py-2"><div className="font-heading text-lg font-bold text-ink leading-none">{v}</div><div className="text-[10px] uppercase tracking-wider text-ink-muted mt-1">{l}</div></div>
          ))}
        </div>
      </Card>

      <DrawingRegister p={p} post={post} />
      <Selections p={p} post={post} />
      <Renders p={p} post={post} />

      <Card testId="qa-checklist">
        <CardHeader title="QA Checklist" icon={CheckCircle2} />
        <ul className="space-y-1.5">
          {p.checklist_live.map((c, i) => (
            <li key={i} className="flex items-center gap-2 text-sm">{c.passed ? <CheckCircle2 className="w-4 h-4 text-st-success" /> : <XCircle className="w-4 h-4 text-st-critical" />}<span className={c.passed ? "text-ink" : "text-ink-muted"}>{c.label}</span></li>
          ))}
        </ul>
        <button onClick={() => submit.mutate()} disabled={submit.isPending || p.status === "awaiting_approval"} data-testid="submit-packet-btn"
          className="mt-4 flex items-center gap-2 bg-ink text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity">
          {submit.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {p.status === "awaiting_approval" ? "Submitted — awaiting owner" : "Submit for owner approval"}
        </button>
        {!p.validation_passed_live && p.status !== "awaiting_approval" && <p className="text-xs text-st-warning mt-2">QA not fully passing — you can still submit; the owner will see the gaps as risks.</p>}
      </Card>
    </div>
  );
}

function MiniForm({ fields, onSubmit, submitLabel, testId, pending }) {
  const [v, setV] = useState({});
  return (
    <div className="flex flex-wrap gap-2 items-end" data-testid={testId}>
      {fields.map((f) => (
        <input key={f.key} type={f.type || "text"} placeholder={f.ph} value={v[f.key] || ""} onChange={(e) => setV({ ...v, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value })}
          data-testid={`${testId}-${f.key}`} className="border border-border px-2 py-1.5 text-sm focus:border-brand focus:outline-none" style={{ width: f.w || 130 }} />
      ))}
      <button onClick={() => { onSubmit(v); setV({}); }} disabled={pending} data-testid={`${testId}-add`} className="bg-brand text-white px-3 py-1.5 text-sm font-semibold disabled:opacity-60">{submitLabel}</button>
    </div>
  );
}

function DrawingRegister({ p, post }) {
  const revise = useMutation({ mutationFn: ({ did, note }) => api.post(`/design-packets/${p.id}/drawings/${did}/revise`, { note }), onSuccess: () => post.reset?.() });
  return (
    <Card testId="drawing-register">
      <CardHeader title="Drawing Register" icon={FileStack} />
      <div className="space-y-1.5 mb-3">
        {(p.drawings || []).map((d) => (
          <div key={d.id} className="flex items-center justify-between border border-border px-3 py-2" data-testid={`drawing-${d.id}`}>
            <div><span className="font-mono text-sm text-ink">{d.number}</span> <span className="text-sm text-ink-muted">{d.name}</span> <span className="text-xs text-ink-muted">· {d.discipline}</span></div>
            <div className="flex items-center gap-2"><span className="text-[10px] font-semibold border border-brand text-brand px-1.5 py-0.5">Rev {d.current_rev}</span>
              <button onClick={() => post.mutate({ path: `/drawings/${d.id}/revise`, body: { note: "Revision issued" } })} className="text-xs border border-border px-2 py-0.5 hover:bg-surface-muted">+ Rev</button></div>
          </div>
        ))}
        {(p.drawings || []).length === 0 && <p className="text-xs text-ink-muted">No drawings yet.</p>}
      </div>
      <MiniForm testId="add-drawing" submitLabel="Add drawing" pending={post.isPending}
        fields={[{ key: "number", ph: "A-101", w: 90 }, { key: "name", ph: "Floor Plan", w: 160 }, { key: "discipline", ph: "Architecture", w: 130 }]}
        onSubmit={(v) => post.mutate({ path: "/drawings", body: v })} />
    </Card>
  );
}

function Selections({ p, post }) {
  return (
    <Card testId="selections-schedule">
      <CardHeader title="Selections Schedule" icon={Palette} />
      <div className="space-y-1.5 mb-3">
        {(p.selections || []).map((s) => (
          <div key={s.id} className={`flex items-center justify-between border px-3 py-2 ${s.at_risk ? "border-st-warning bg-st-warning/5" : "border-border"}`} data-testid={`selection-${s.id}`}>
            <div><span className="text-sm text-ink">{s.item}</span> <span className="text-xs text-ink-muted">· {s.category}{s.spec && ` · ${s.spec}`}</span></div>
            <div className="flex items-center gap-2 text-xs">{s.at_risk && <AlertTriangle className="w-3.5 h-3.5 text-st-warning" />}<span className="text-ink-muted">{s.lead_time_weeks}w lead</span></div>
          </div>
        ))}
        {(p.selections || []).length === 0 && <p className="text-xs text-ink-muted">No selections yet.</p>}
      </div>
      <MiniForm testId="add-selection" submitLabel="Add selection" pending={post.isPending}
        fields={[{ key: "category", ph: "Tile", w: 100 }, { key: "item", ph: "Calacatta", w: 150 }, { key: "spec", ph: "24x48", w: 90 }, { key: "lead_time_weeks", ph: "weeks", type: "number", w: 80 }]}
        onSubmit={(v) => post.mutate({ path: "/selections", body: v })} />
    </Card>
  );
}

function Renders({ p, post }) {
  return (
    <Card testId="renders">
      <CardHeader title="Renders" icon={ImageIcon} />
      <div className="grid sm:grid-cols-2 gap-2 mb-3">
        {(p.renders || []).map((r) => (
          <div key={r.id} className="border border-border p-2" data-testid={`render-${r.id}`}>
            <div className="flex items-center justify-between"><span className="text-sm text-ink">{r.scene}</span><span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 border ${r.status === "complete" ? "border-st-success text-st-success" : "border-border text-ink-muted"}`}>{r.status}</span></div>
            {r.image_urls?.length > 0 && <img src={r.image_urls[0]} alt={r.scene} className="w-full h-28 object-cover mt-2 bg-surface-muted" onError={(e) => { e.target.style.display = "none"; }} />}
          </div>
        ))}
        {(p.renders || []).length === 0 && <p className="text-xs text-ink-muted">No renders requested.</p>}
      </div>
      <MiniForm testId="request-render" submitLabel="Request render" pending={post.isPending}
        fields={[{ key: "scene", ph: "Living room", w: 200 }]}
        onSubmit={(v) => post.mutate({ path: "/render-request", body: v })} />
    </Card>
  );
}
